import React, { useState } from 'react';
import SelectionCard from '../SelectionCard';
import { cn } from '../../../lib/utils';

/**
 * DomainCardsSection - Select 2 Level 1 domain cards
 */
function DomainCardsSection({ character, onUpdate, allData }) {
  const domainCards = allData.domainCards || {};
  const [selectedDomain, setSelectedDomain] = useState('all');

  // Get character's available domains
  const classDomains = allData.classes?.[character.class]?.domains || [];

  // Filter cards by level 1 and character's domains
  const availableCards = Object.entries(domainCards).filter(([id, card]) =>
    card.level === 1 && classDomains.includes(card.domain)
  );

  // Get unique domains from available cards for filtering
  const uniqueDomains = [...new Set(availableCards.map(([, card]) => card.domain))];

  // Filter cards by selected domain filter
  const filteredCards = selectedDomain === 'all'
    ? availableCards
    : availableCards.filter(([, card]) => card.domain === selectedDomain);

  function handleCardToggle(cardId) {
    const currentLoadout = character.loadout || [];

    if (currentLoadout.includes(cardId)) {
      // Remove card
      onUpdate({
        loadout: currentLoadout.filter(id => id !== cardId)
      });
    } else {
      // Add card (max 2 for level 1)
      if (currentLoadout.length < 2) {
        onUpdate({
          loadout: [...currentLoadout, cardId]
        });
      }
    }
  }

  const selectedCount = character.loadout?.length || 0;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-heading-sm text-red-400 font-serif mb-2">Choose 2 Domain Cards</h4>
        <p className="text-gray-300 text-small mb-4">
          Select 2 Level 1 cards from your class domains.
          Selected: <span className={cn(
            "font-bold",
            selectedCount === 2 ? "text-green-400" : "text-yellow-400"
          )}>{selectedCount}/2</span>
        </p>

        {/* Domain filter */}
        {uniqueDomains.length > 1 && (
          <div className="flex gap-2 mb-4 flex-wrap">
            <button
              onClick={() => setSelectedDomain('all')}
              className={cn(
                "px-3 py-1.5 rounded text-small font-medium transition-colors",
                selectedDomain === 'all'
                  ? "bg-primary text-white"
                  : "bg-surface-2 text-gray-300 hover:bg-surface-3"
              )}
            >
              All Domains
            </button>
            {uniqueDomains.map(domain => (
              <button
                key={domain}
                onClick={() => setSelectedDomain(domain)}
                className={cn(
                  "px-3 py-1.5 rounded text-small font-medium transition-colors capitalize",
                  selectedDomain === domain
                    ? "bg-primary text-white"
                    : "bg-surface-2 text-gray-300 hover:bg-surface-3"
                )}
              >
                {domain}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cards grid */}
      {!character.class ? (
        <p className="text-gray-400 text-center py-8">
          Select a class first to see available domain cards.
        </p>
      ) : filteredCards.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {filteredCards.map(([id, card]) => {
            const isSelected = character.loadout?.includes(id);

            return (
              <SelectionCard
                key={id}
                title={card.name}
                subtitle={`${card.domain} | Level ${card.level} | ${card.type || 'Action'}`}
                description={card.effect || card.text || ''}
                isSelected={isSelected}
                onClick={() => handleCardToggle(id)}
                showDetails={true}
              />
            );
          })}
        </div>
      ) : (
        <p className="text-gray-400 text-center py-8">
          No Level 1 cards available for this domain filter.
        </p>
      )}

      {selectedCount >= 2 && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4">
          <p className="text-green-400 text-small">
            ✓ 2 domain cards selected! You're ready to continue.
          </p>
        </div>
      )}
    </div>
  );
}

export default DomainCardsSection;
